import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import matchSorter from 'match-sorter';
import {
  FormGroup,
  FormText,
  Label,
  UncontrolledTooltip,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { fetchTerms } from '../../actions/Metadata';

import { APIcreateTerm } from '../../api/Metadata';

import UploadFileTaxonomyMenuList from './FileTaxonomyMenuList';
import UploadFileTaxonomyMultiValue from './FileTaxonomyMultiValue';

import {
  slugify,
  sendTrackingEvent,
} from '../../helpers';
import { userHasFeature } from '../../helpers/Features';
import { performOnboardingStep } from '../../helpers/Onboarding';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { getSetting } from '../../helpers/Settings';
import { getTaxonomyRoleSetting, hasTaxonomyRoleSettingAllowed } from '../../helpers/Metadata';

class UploadFileTaxonomy extends Component {
  constructor(props) {
    super(props);

    this.state = {
      values: [],
      hints: [],
      creatingTerm: false,
      inputValue: '',
    };

    this.handleCreate = this.handleCreate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleAddValue = this.handleAddValue.bind(this);
    this.handleAddAllHints = this.handleAddAllHints.bind(this);
  }

  componentDidMount() {
    this.updateValues();
  }

  componentDidUpdate(prevProps) {
    const { selectedFiles } = this.props;
    if (selectedFiles.length !== prevProps.selectedFiles.length) {
      this.updateValues();
    }
  }

  getTermsOptions() {
    const { taxonomy } = this.props;
    const options = [];

    if (taxonomy.terms) {
      taxonomy.terms.filter(t => !t.blocked).forEach((term) => {
        options.push({
          value: `${term.id}`,
          label: term.name,
          blocked: term.blocked,
        });
      });
    }

    return options;
  }

  getBlockedTaxonomyTermsNames() {
    const { taxonomy } = this.props;
    return taxonomy.terms.filter(t => t.blocked).map(t => (
      { name: t.name, name_lowercase: t.name.toLowerCase() }
    ));
  }

  updateValues() {
    const {
      selectedFiles,
      taxonomy,
      initialTermsForHints,
    } = this.props;
    const values = [];

    selectedFiles.forEach((file) => {
      const terms = _.uniqBy(file.terms, 'id').filter(t => (t.taxonomy_id === taxonomy.id));
      terms.forEach((term) => {
        const vindex = values.findIndex(v => (v.value === `${term.id}`));
        if (vindex >= 0) {
          values[vindex].count += 1;
        } else {
          values.push({
            value: `${term.id}`,
            label: term.name,
            count: 1,
          });
        }
      });
    });

    let hints = [];
    if (initialTermsForHints) {
      hints = initialTermsForHints.map(it => ({
        value: it.id,
        label: it.name,
        count: values.filter(v => (parseInt(v.value) === it.id)).length > 0 ? 1 : 0,
      })).filter(h => (h.count !== selectedFiles.length));
    } else {
      hints = values.filter(v => (v.count !== selectedFiles.length));
    }
    this.setState({
      values: values.filter(v => (v.count === selectedFiles.length)),
      hints,
    });
  }

  handleCreate(value) {
    const {
      dispatch,
      taxonomy,
      intl,
    } = this.props;

    if (this.getBlockedTaxonomyTermsNames()
      .find(bt => bt.name_lowercase === value.trim().toLowerCase())
    ) {
      return;
    }

    this.setState({ creatingTerm: true });
    APIcreateTerm(taxonomy.id, { name: value }).then((response) => {
      dispatch(fetchTerms(taxonomy.id)).then(() => {
        this.handleAddValue({
          value: response.data.id,
          label: response.data.name,
        });
      });
    }).catch((e) => {
      if (e.response.status === 409) {
        addFlashMessage({
          type: 'danger',
          text: intl.formatMessage({
            defaultMessage: 'Term with given name already exists',
          }),
        });
      }
    }).finally(() => {
      this.setState({ creatingTerm: false });
    });
  }

  handleChange(changedValues) {
    const {
      taxonomy,
      handleTaxonomyChange,
    } = this.props;
    const { values: currentValues } = this.state;

    const newValues = [];
    const values = changedValues || [];
    values.forEach((value) => {
      newValues.push({
        id: parseInt(value.value),
        taxonomy_id: taxonomy.id,
        name: value.label,
      });
      sendTrackingEvent('tags-added');
    });

    performOnboardingStep('tags-added');

    let removed = null;
    if (values.length < currentValues.length) {
      removed = window._.differenceBy(currentValues, values, 'value');
    }

    handleTaxonomyChange(newValues, taxonomy.id, removed);
    this.setState({
      values,
    });
  }

  handleAddValue(value) {
    const { values } = this.state;
    values.push(value);
    this.setState({ values }, () => {
      this.handleChange(values);
    });
  }

  handleAddAllHints(e) {
    e.preventDefault();
    const { hints } = this.state;
    hints.forEach((hint) => {
      this.handleAddValue(hint);
    });
  }

  render() {
    const {
      intl,
      taxonomy,
      initialTermsForHints,
      selectedFiles,
      uploadModal,
      forceRequiredError,
      isEdit,
    } = this.props;
    const {
      values,
      hints,
      creatingTerm,
      inputValue,
    } = this.state;

    const showTaxonomyRequiredError = forceRequiredError || !!selectedFiles.find(sf => {
      if (sf.errors && sf.errors.taxonomy && sf.errors.taxonomy.find(t => (t === taxonomy.id))) {
        return true;
      }
      return false;
    });

    const blockedTaxonomyTermsNames = this.getBlockedTaxonomyTermsNames();

    const termBlocked = blockedTaxonomyTermsNames
      .find((bt) => bt.name_lowercase === inputValue.trim().toLowerCase());

    const getPlaceholder = () => {
      if (getTaxonomyRoleSetting(taxonomy, 'read_only') === true) {
        return intl.formatMessage({ defaultMessage: 'This Tag Category is empty and read only' });
      }

      if (taxonomy.creatable) {
        return intl.formatMessage({ defaultMessage: 'search and select or create' });
      }

      return intl.formatMessage({ defaultMessage: 'search and select' });
    };

    if (
      isEdit &&
      taxonomy.ai_tagging &&
      (values.length === 0 || !getSetting('ai_tagging'))
    ) {
      return null;
    }

    return (
      <FormGroup
        className={classnames(
          `taxonomy-${slugify(taxonomy.name || taxonomy.plural)}`,
          {
            'ai-tags': taxonomy.rekognition,
            taxonomy: !taxonomy.rekognition,
          },
        )}
      >
        <Label
          for={`taxonomy-${taxonomy.id}`}
          className={classnames({
            disabled:
          (
            !userHasFeature('custom-categories') && (!taxonomy.standard && !taxonomy.ai_tagging)) ||
            getTaxonomyRoleSetting(taxonomy, 'read_only') === true,
          })}
        >
          {taxonomy.plural}
          {taxonomy.required && (
            <span
              className="required-asterisk"
              id={`taxonomy-required-asterisk-${taxonomy.id}`}
            >
              *
              <UncontrolledTooltip fade={false} placement="bottom" target={`taxonomy-required-asterisk-${taxonomy.id}`}>
                {intl.formatMessage({ defaultMessage: 'Required' })}
              </UncontrolledTooltip>
            </span>
          )}
          {taxonomy.rekognition && (
            <FormText color="muted">
              {intl.formatMessage({ defaultMessage: 'This field is for AI generated tags only. You can remove incorrect tags.' })}
            </FormText>
          )}
        </Label>
        {termBlocked && (
          <div className="invalid-feedback mt-0 mb-1" style={{ display: 'block' }}>
            {intl.formatMessage({ defaultMessage: 'The tag <strong>{termName}</strong> is blocked in this Tag Category and cannot be added.' }, { termName: termBlocked.name })}
          </div>
        )}
        {taxonomy.ai_tagging ? (
          <Select
            id={`taxonomy-${taxonomy.id}`}
            classNamePrefix="taxonomy-select"
            components={{
              MultiValue: (props) => (
                <UploadFileTaxonomyMultiValue {...props} showBlocked={false} />
              ),
              MenuList: UploadFileTaxonomyMenuList,
              DropdownIndicator: null,
            }}
            isMulti
            isSearchable={false}
            isDisabled={creatingTerm || (!userHasFeature('custom-categories') && (!taxonomy.standard && !taxonomy.ai_tagging)) || getTaxonomyRoleSetting(taxonomy, 'read_only') === true}
            isLoading={creatingTerm}
            placeholder=""
            menuIsOpen={false}
            filterOption={false}
            onInputChange={(v) => { this.setState({ inputValue: v }); }}
            onChange={this.handleChange}
            value={values}
            onMenuOpen={() => {}}
            onMenuClose={() => {}}
            menuPortalTarget={uploadModal ? undefined : document.querySelector('.file-modal')}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        ) : (
          <CreatableSelect
            id={`taxonomy-${taxonomy.id}`}
            classNamePrefix="taxonomy-select"
            components={{
              MultiValue: (props) => (
                <UploadFileTaxonomyMultiValue {...props} showBlocked={false} />
              ),
              MenuList: UploadFileTaxonomyMenuList,
            }}
            isMulti
            isDisabled={creatingTerm || (!userHasFeature('custom-categories') && (!taxonomy.standard && !taxonomy.ai_tagging)) || getTaxonomyRoleSetting(taxonomy, 'read_only') === true}
            isLoading={creatingTerm}
            placeholder={getPlaceholder()}
            options={matchSorter(this.getTermsOptions(), inputValue, { keys: ['label'] })}
            filterOption={false}
            onInputChange={(v) => { this.setState({ inputValue: v }); }}
            isValidNewOption={(input, selectValue, selectOptions) => {
              if (!taxonomy.creatable) {
                return false;
              }
              if (
                input.trim().length === 0 ||
                selectOptions.find(option => option.label === input)
              ) {
                return false;
              }
              if (blockedTaxonomyTermsNames
                .find(bt => bt.name_lowercase === input.trim().toLowerCase())) {
                return false;
              }
              return true;
            }}
            onChange={this.handleChange}
            onCreateOption={this.handleCreate}
            value={values}
            menuPortalTarget={uploadModal ? undefined : document.querySelector('.file-modal')}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            noOptionsMessage={() => {
              if (!hasTaxonomyRoleSettingAllowed(taxonomy, 'can_add')) {
                return intl.formatMessage({ defaultMessage: 'Adding new tags is blocked.' });
              }
              return intl.formatMessage({ defaultMessage: 'Nothing found' });
            }}
          />
        )}
        {showTaxonomyRequiredError && (
          <div className="invalid-feedback" style={{ display: 'block' }}>
            {intl.formatMessage({ defaultMessage: 'This field is required' })}
          </div>
        )}
        {(!taxonomy.ai_tagging && hints.length > 0) && (
          <FormText color="muted">
            {initialTermsForHints ? (
              intl.formatMessage({
                defaultMessage: 'Other {taxonomy} from files selected to merge: ',
              }, { taxonomy: taxonomy.plural.toLowerCase() })
            ) : (
              intl.formatMessage({
                defaultMessage: 'Other {taxonomy} from selected files: ',
              }, { taxonomy: taxonomy.plural.toLowerCase() })
            )}
            <ul className="upload-file-taxonomy-list upload-file-tags-list">
              {hints.map(hint => (
                <li key={`${taxonomy.id}-${hint.value}`} className="upload-file-taxonomy-item">
                  <a
                    href="#add-term"
                    id={`upload-file-${taxonomy.id}-${hint.value}`}
                    onClick={(e) => {
                      e.preventDefault();
                      this.handleAddValue(hint);
                    }}
                  >
                    {hint.label}
                  </a>
                  <UncontrolledTooltip placement="bottom" target={`upload-file-${taxonomy.id}-${hint.value}`}>
                    {initialTermsForHints ? (
                      intl.formatMessage({ defaultMessage: 'Add to merged file' })
                    ) : (
                      intl.formatMessage({ defaultMessage: 'Add to all selected files' })
                    )}
                  </UncontrolledTooltip>
                </li>
              ))}
            </ul>
            <a
              href="#add-all-terms"
              id={`upload-file-${taxonomy.id}-all`}
              onClick={this.handleAddAllHints}
            >
              {initialTermsForHints ? (
                intl.formatMessage({ defaultMessage: 'Add to merged file' })
              ) : (
                intl.formatMessage({ defaultMessage: 'Add to all selected files' })
              )}
            </a>
            <UncontrolledTooltip placement="bottom" target={`upload-file-${taxonomy.id}-all`}>
              {initialTermsForHints ? (
                intl.formatMessage({ defaultMessage: 'Add all attributes to merged file' })
              ) : (
                intl.formatMessage({ defaultMessage: 'Add all attributes to all selected files' })
              )}
            </UncontrolledTooltip>
          </FormText>
        )}
      </FormGroup>
    );
  }
}

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  uploadModal: state.Files.uploadModal,
});

UploadFileTaxonomy.propTypes = {
  uploadModal: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  taxonomy: PropTypes.object.isRequired,
  initialTermsForHints: PropTypes.array,
  handleTaxonomyChange: PropTypes.func.isRequired,
  forceRequiredError: PropTypes.bool,
  isEdit: PropTypes.bool,
};

UploadFileTaxonomy.defaultProps = {
  initialTermsForHints: null,
  forceRequiredError: false,
  isEdit: false,
};

export default injectIntl(connect(mapStateToProps)(UploadFileTaxonomy));
